/* eslint-disable @typescript-eslint/no-shadow */
import {useRoutes} from 'react-router-dom'
import {
  createContext,
  ReactNode,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {ACCESS_TOKEN_KEY, MIN_TOKEN_VALIDITY_MINUTE, REFRESH_TOKEN_KEY} from 'sr/constants/common'
import fetchTokenFromRefreshToken from 'sr/utils/api/fetchTokenFromRefreshToken'
import logoutUser from 'sr/utils/api/logoutUser'
import {getCookieValue, persistToken, removeLocalStorageKey} from 'sr/utils/helper'
import parseJWT from 'sr/utils/parseToken'
import {setReadOnlyStorage} from 'sr/helpers/ui-components/ReadOnlyModeProvider'
import {clearSession} from 'sr/utils/sessionTracker'
import {clearSessionListState} from 'sr/helpers/hooks/useSessionState'

interface AuthContextProps {
  isAuthenticated: boolean
  isAuthReady: boolean
  persistTokens: VoidFunction
  logout: VoidFunction
}

const AuthContext = createContext<AuthContextProps>({
  isAuthenticated: false,
  isAuthReady: false,
  logout: () => undefined,
  persistTokens: () => undefined,
})

// use auth context
const useAuth = () => {
  return useContext(AuthContext)
}

interface Props {
  children: ReactNode
}

const getTokenExpiryTime = (token: string | null) => {
  if (token) {
    const {exp} = parseJWT(token)
    return new Date(exp * 1000).getTime()
  }
  return null
}

const checkTokenExpiry = (tokenExpiryTime: number | null, minValidity = 60000) => {
  if (tokenExpiryTime != null) {
    const currentTime = new Date().getTime()
    return tokenExpiryTime - currentTime <= minValidity
  }
  return false
}

const minValidity = MIN_TOKEN_VALIDITY_MINUTE * 60 * 1000

const AuthProvider = ({children}: Props) => {
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [isAuthReady, setIsAuthReady] = useState(false)

  const accessTokenExpiryTime = useMemo(() => getTokenExpiryTime(accessToken), [accessToken])

  const refreshTokenExpiryTime = useMemo(() => getTokenExpiryTime(refreshToken), [refreshToken])

  const logout = useCallback(async () => {
    if (refreshToken) {
      const response = await logoutUser(refreshToken)
      setAccessToken(null)
      setRefreshToken(null)
      persistToken(ACCESS_TOKEN_KEY, '', -1)
      persistToken(REFRESH_TOKEN_KEY, '', -1)
      // Clear session tracking data
      clearSession()
      // Drop persisted grid filters so the next admin starts clean
      clearSessionListState()
      // Reset to Read-Only mode on logout for security
      setReadOnlyStorage(true)
      window.location.reload()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken])
  const persistTokens = useCallback(() => {
    setIsAuthReady(false)
    const accessToken = getCookieValue(ACCESS_TOKEN_KEY) || null
    const refreshToken = getCookieValue(REFRESH_TOKEN_KEY) || null
    setAccessToken(accessToken)
    setRefreshToken(refreshToken)
    setIsAuthReady(true)
  }, [])

  useEffect(() => {
    persistTokens()
  }, [persistTokens])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated:!!refreshToken,
        isAuthReady,
        logout,
        persistTokens,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export {useAuth, AuthProvider}
